var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { produce } from 'immer';
import { OmniStyleElement, OmniIconElement } from 'omni-ui';
import { om2alert } from 'omni-campaign-ui';
import { constants as teamsConstants, MicrosoftAuthenticationHandler, } from 'omni-teams-sdk';
import { html, nothing, repeat, ref, createRef, classMap, property, state } from 'omni-ui/lit';
import { OmniElement } from 'omni-ui/omni-element.js';
import 'omni-ui/omni-loading-indicator.js';
import { Editor } from '@tiptap/core';
import Mention from '@tiptap/extension-mention';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import { debounceAsync, envFromHostname } from './helpers/utils.js';
import OmniAPI from './services/omni-api.js';
import styles from './omni-wysiwyg.css.js';
const { TEAMS_SEARCH_CHAT, TEAMS_SEARCH_PEOPLE, TEAMS_SEARCH_CHANNEL, CATEGORY_OPTIONS, } = teamsConstants;
OmniStyleElement.register();
OmniIconElement.register();
/**
 * Component for a rich text editor based on the TipTap library. It delegates the content state management to TipTap, sending
 * change events with each update. If you must force a content update from another component, or if reusing the same instance,
 * you can call the public {@link OmniWYSIWYG.setContent} method on the instance of the element.
 */
export class OmniWYSIWYG extends OmniElement {
    constructor() {
        super(...arguments);
        /**
         * The environment to use when calling the Omni API for MS Auth
         * @defaultValue
         * Defaulted to `Environment` based on the URL
         */
        this.env = envFromHostname() || 'local';
        /**
         * The current client ID, used for grabbing MS Teams settings
         * @defaultValue
         * Retrieved from client preferences API if not passed
         */
        this.clientId = null;
        /** Whether to render HTML or the editor interface */
        this.displayOnly = false;
        /**
         * Whether we want to show overflow or hide it with CSS rule
         * @see {@link OmniWYSIWYG.overflowed}
         */
        this.overflowVisible = false;
        /**
         * Tracks & reflects if the editor contents have overflowed the
         * containing div, styled by the `:host[overflowed]` CSS ruleset
         * which adds a gradient fade out effect to the bottom
         * @todo fix this, the detection doesn't seem to be working anymore
         */
        this.overflowed = false;
        /**
         * Whether the editor should be automatically focused within
         * when switching to a view that contains the editor
         */
        this.shouldAutofocus = false;
        /** Placeholder text for the editor */
        this.placeholder = 'Enter text';
        /**
         * Prepopulated editor content. This isn't reactive, as it's only
         * used when the WYSIWYG is initially mounted. To update the WYSIWYG
         * content, see {@link OmniWYSIWYG.setContent}
         */
        this.initialContent = '';
        /**
         * Whether editor should be allow mentioning MS Teams users
         * and, if so, whether to initialize MS Teams auth & retrieve
         * user and client data from Omni.
         */
        this.enableMentions = false;
        /** Which MS Teams objects to enable search for when mentioning users */
        this.mentionTypes = CATEGORY_OPTIONS;
        /** Object containing the buttons to be displayed in the editor toolbar */
        this.menuButtons = {};
        /** Whether a tag search is in-progress */
        this.loadingMentions = false;
        /** A filtered list of teams users matching the name searched */
        this.mentionMatches = [];
        /**
         * The currently "selected" index of {@OmniWYSIWYG.mentionMatches},
         * if mentions are enabled.
         * @todo should we default this to a number if it's not used?
         */
        this.mentionItemSelected = 0;
        /** Stored Teams options from the Omni API to use with MS Graph/Teams SDK */
        this.teamOptions = null;
        /** Tracking the initial load in cases when MS auth is enabled */
        this.loadingInitSetup = this.enableMentions;
        /** WYSYIWYG container */
        this.wysiwygRef = createRef();
        /** Mention list popover container */
        this.mentionListRef = createRef();
        /**
         * An instance of the MS Auth API
         * @todo should we initialize this elsewhere in case mentions are disabled?
         */
        this.msAuth = new MicrosoftAuthenticationHandler();
        this.#suggestionConfig = {
            allowSpaces: true,
            items: async ({ query }) => {
                /** Reset the query list so old matches don't hang around */
                this.mentionMatches = [];
                if (!query)
                    return this.mentionMatches;
                this.#setMentionPositions();
                this.loadingMentions = true;
                this.mentionMatches = await this.#getMentions(query);
                this.loadingMentions = false;
                return this.mentionMatches;
            },
            render: () => ({
                onStart: () => {
                    this.mentionItemSelected = 0;
                },
                onUpdate: (props) => {
                    //this.mentionMatches = props.items;
                    this.mentionCommand = props.command;
                    this.#setMentionPositions();
                },
                onKeyDown: (props) => {
                    if (!['escape', 'enter', 'arrowup', 'arrowdown'].includes(props.event.key?.toLowerCase()))
                        return false;
                    if (props.event.key === 'Escape') {
                        this.mentionMatches = [];
                        return false;
                    }
                    if (props.event.key === 'Enter') {
                        this.#selectMention();
                    }
                    if (props.event.key === 'ArrowUp') {
                        if (this.mentionItemSelected > 0) {
                            this.mentionItemSelected -= 1;
                        }
                        else {
                            this.mentionItemSelected = this.mentionMatches.length - 1;
                        }
                    }
                    if (props.event.key === 'ArrowDown') {
                        if (this.mentionMatches.length - 1 > this.mentionItemSelected) {
                            this.mentionItemSelected += 1;
                        }
                        else {
                            this.mentionItemSelected = 0;
                        }
                    }
                    const i = this.mentionItemSelected;
                    const item = this.shadowRoot.querySelector(`.mention-item:nth-child(${i + 1})`);
                    this.mentionListRef.value.getBoundingClientRect();
                    const { top: mentionListTop, height: mentionListHeight } = this.mentionListRef.value.getBoundingClientRect();
                    item.getBoundingClientRect();
                    const { top: itemTop } = item.getBoundingClientRect();
                    if (mentionListTop + mentionListHeight < itemTop ||
                        mentionListTop > itemTop) {
                        this.mentionListRef.value.scrollTop = item.offsetTop;
                    }
                    return true;
                },
                onExit: () => {
                    this.mentionMatches = [];
                },
            }),
        };
        this.#setupMenuButtons = () => {
            /**
              * Manually specifying actions is necessary to track changes in toggle state
            * for buttons like numberedList and bulletList
              */
            const defaultExtensions = {
                undo: {
                    icon: 'omni:editor:undo',
                    method: 'undo',
                    tooltip: 'Undo',
                    order: 0,
                },
                redo: {
                    icon: 'omni:editor:redo',
                    method: 'redo',
                    tooltip: 'Redo',
                    order: 1,
                },
                bold: {
                    icon: 'omni:editor:bold',
                    method: 'toggleBold',
                    tooltip: 'Bold',
                    order: 2,
                },
                italic: {
                    icon: 'omni:editor:italics',
                    method: 'toggleItalic',
                    tooltip: 'Italize',
                    order: 3,
                },
                underline: {
                    icon: 'omni:editor:underline',
                    method: 'toggleUnderline',
                    tooltip: 'Underline',
                    order: 4,
                },
                bulletList: {
                    icon: 'omni:editor:bulletList',
                    method: 'toggleBulletList',
                    tooltip: 'Bullet List',
                    order: 5,
                },
                orderedList: {
                    icon: 'omni:editor:numberList',
                    method: 'toggleOrderedList',
                    tooltip: 'Number List',
                    order: 6,
                },
            };
            this.menuButtons = this.enabledExtensions?.length
                ? this.enabledExtensions.reduce((acc, extension) => {
                    let name = extension;
                    switch (extension.toLowerCase()) {
                        case 'bl':
                            name = 'bulletList';
                            break;
                        case 'ol':
                            name = 'orderedList';
                            break;
                        case 'b':
                            name = 'bold';
                            break;
                        case 'i':
                            name = 'italic';
                            break;
                        case 'u':
                            name = 'underline';
                            break;
                        default:
                            break;
                    }
                    // eslint-disable-next-line no-param-reassign
                    acc[name] = defaultExtensions[name];
                    return acc;
                }, {})
                : defaultExtensions;
        };
        this.#getMentions = debounceAsync(async (query) => {
            let userResults = [];
            let chatResults = [];
            let channelResults = [];
            try {
                if (this.mentionTypes.includes(TEAMS_SEARCH_PEOPLE)) {
                    userResults = await this.msAuth.getPeople(query);
                }
                if (this.mentionTypes.includes(TEAMS_SEARCH_CHAT)) {
                    chatResults = await this.msAuth.getChats(query);
                }
                if (this.mentionTypes.includes(TEAMS_SEARCH_CHANNEL)) {
                    channelResults = await Promise.all(this.teamOptions?.map(({ id, displayName }) => this.msAuth.getChannels(id, displayName, query)));
                }
            }
            catch (e) {
                let message = e;
                if (e instanceof Response) {
                    message = await e.text();
                }
                // eslint-disable-next-line no-console
                console.warn('Failed to get team members.', e);
                om2alert(String(message) ||
                    'Failed to get team members. Please try again later.');
            }
            return [...userResults, ...chatResults, ...channelResults.flat()].map(({ id, displayName, teamName, teamId, members = [] }) => {
                let type = members.length > 1 ? TEAMS_SEARCH_CHAT : TEAMS_SEARCH_PEOPLE;
                if (teamName?.trim() && teamId?.trim())
                    type = TEAMS_SEARCH_CHANNEL;
                return {
                    id,
                    type,
                    teamId,
                    displayName: type === TEAMS_SEARCH_CHANNEL
                        ? `${teamName}: ${displayName}`
                        : displayName,
                };
            });
        }, 300);
        this.#selectMention = () => {
            const teamsUser = this.mentionMatches[this.mentionItemSelected];
            this.mentionCommand({
                id: teamsUser.id,
                label: teamsUser.displayName,
                type: teamsUser.type,
                teamId: teamsUser.teamId,
            });
        };
        this.#setMentionPositions = () => {
            const { left: containerLeft, top: containerTop } = this.getBoundingClientRect();
            // The mention$ editor state prop contains any currently-active tag searches
            const { state: { mention$: { decorationId }, },
            /** @todo figure out tiptap's typing with custom state/mentions setup */
             } = this.editor;
            const mentionPosition = this.shadowRoot
                .querySelector(`[data-decoration-id=${decorationId}]`)
                ?.getBoundingClientRect();
            if (!mentionPosition)
                return;
            // Position variables for the results list
            this.style.setProperty('--mention-list-x', `${mentionPosition.left - containerLeft}px`);
            this.style.setProperty('--mention-list-y', `${mentionPosition.top - containerTop + mentionPosition.height}px`);
            // Position variables for the in-text loading indicator
            this.style.setProperty('--loader-size', 
            // Add some size for visual clarity & text differentiation
            `${mentionPosition.height + 6}px`);
            this.style.setProperty('--loader-x', `${mentionPosition.left + mentionPosition.width + 3 - containerLeft}px`);
            this.style.setProperty('--loader-y', `${mentionPosition.top - containerTop}px`);
        };
        /**
         * Public method for use outside of the component with ref directive
         * to force an editor content update
         * @param {string} content - string being passed into editor
         */
        this.setContent = (content) => {
            this.editor.commands.setContent(content);
        };
        /**
         * Grabs the content of the editor as an HTML string, checks if its an empty <p> tag, and then dispatch the content as the payload for change event
         * @param editor - instance of the editor
         * @event {CustomChangeEvent}
         */
        this.#handleContentChange = ({ editor }) => {
            const htmlContent = editor.getHTML();
            const textContent = editor.view.dom.innerText;
            let content = editor.getJSON();
            if (editor.getHTML().replace(/\s/g, '') === '<p></p>')
                content = '';
            this.dispatchNewEvent('change', {
                detail: { value: content, html: htmlContent, text: textContent },
            });
        };
        /**
         * Detect overflow and update property that is reflected as html attribute
         */
        this.#detectOverflow = async () => {
            if (this.overflowVisible)
                return;
            await this.updateComplete; // Wait for next render to occur (if any) before continuing
            const { value: wysiwygElem } = this.wysiwygRef;
            if (wysiwygElem) {
                this.overflowed =
                    wysiwygElem.clientHeight < wysiwygElem.firstElementChild.clientHeight;
            }
        };
        this.#updateButtonStates = () => {
            this.menuButtons = produce(this.menuButtons, draft => {
                Object.keys(draft).forEach((name) => {
                    draft[name].active = this.editor.isActive(name);
                });
            });
        };
        this.#handleMenuButtonClick = (method) => {
            /** @todo solve TS issues */
            this.editor.chain().focus()[method]().run();
            this.#updateButtonStates();
        };
    }
    /** Custom styles */
    static get styles() {
        return [...super.styles, styles];
    }
    #suggestionConfig;
    /**
     * Allow externally setting the content of this controller to plain text. Used by
     * CampaignAssistField.
     *
     * @param {string} text - The plain text content that should be set
     */
    set value(text) {
        this.editor.commands.clearContent();
        this.editor.commands.insertContent(text);
    }
    updated(cp) {
        if (this.editor && cp.has("displayOnly")) {
            this.editor.setEditable(!this.displayOnly);
        }
    }
    #setupMenuButtons;
    /**
     * Sets the environment to use for calling Omni API, initializes MS Teams/auth
     * & creates an instance of Tiptap Editor with the desired extensions and the
     * values for the editors properties shouldAutofocus, displayOnly, and
     * content. Sets {@link #handleContentChange} as the callback for the editor's
     * on update event.
     */
    async firstUpdated() {
        this.loadingInitSetup = !this.displayOnly && this.enableMentions;
        // If mentions are disabled or you can't edit, set mentionTypes to empty
        if (!this.enableMentions || this.displayOnly)
            this.mentionTypes = [];
        this.#setupMenuButtons();
        const defaultExtensions = [
            StarterKit.configure(),
            Underline,
            Placeholder.configure({
                placeholder: this.placeholder,
            }),
        ];
        /**
         * Keep mention setup in displayOnly mode because tiptap needs to
         * support the "mention" type of content node
         */
        if (this.enableMentions) {
            const MentionCustom = Mention.extend({
                /**
                 * Keep track of the type of teams result & the team ID for use by
                 * parents w/ Teams SDK or MS Graph
                 */
                addAttributes() {
                    return {
                        ...this.parent(),
                        type: {
                            default: TEAMS_SEARCH_PEOPLE,
                        },
                        teamId: {
                            default: '',
                        },
                    };
                },
            });
            defaultExtensions.push(MentionCustom.configure({
                HTMLAttributes: {
                    class: 'mention',
                },
                suggestion: this.#suggestionConfig,
            }));
            /**
             * Only initialize Microsoft auth handler if tagging is enabled
             */
            if (!this.displayOnly) {
                await this.msAuth.init();
            }
        }
        if (!this.displayOnly && this.enableMentions) {
            const omniApi = new OmniAPI(this.env);
            const { settings: teamsSettings } = await omniApi.getTeamsPreferences(this.userId, this.ansid);
            // Get the default client from client settings if it's not set
            if (!this.clientId) {
                const { settings: { default: id = '' }, } = await omniApi.getClientPreferences(this.userId, this.ansid);
                this.clientId = id;
            }
            const visibleTeams = teamsSettings[this.clientId] || [];
            const filterFn = (team) => visibleTeams.includes(team.id);
            /**
             * @todo await vals instead of chaining & add try/catch to this method
             */
            await this.msAuth
                .getTeams(filterFn)
                .then((res) => {
                this.teamOptions = res;
            })
                .catch((error) => {
                // eslint-disable-next-line no-console
                console.warn('Error getting Teams', error);
                if (error.errorCode === 'user_cancelled') {
                    // User cancelled the dialog, so we'll just close the modal.
                    // eslint-disable-next-line no-console
                    console.warn('User cancelled MS Teams authentication');
                }
                else {
                    const errorMsg = `We're unable to connect to Teams. Please try again.`;
                    /** @todo add a custom event for errors & die gracefully */
                    // eslint-disable-next-line no-console
                    console.error(errorMsg);
                }
            });
        }
        this.editor = new Editor({
            element: this.wysiwygRef.value,
            extensions: defaultExtensions,
            autofocus: this.shouldAutofocus,
            editable: !this.displayOnly,
            content: this.initialContent,
        });
        this.editor.on('update', this.#handleContentChange);
        this.editor.on('selectionUpdate', this.#updateButtonStates);
        // When this component changes dimensions re-check for overflow
        new ResizeObserver(this.#detectOverflow).observe(this);
        this.#detectOverflow();
        this.loadingInitSetup = false;
    }
    #getMentions;
    #selectMention;
    #setMentionPositions;
    /**
     * Grabs the content of the editor as an HTML string, checks if its an empty <p> tag, and then dispatch the content as the payload for change event
     * @param editor - instance of the editor
     * @event {CustomChangeEvent}
     */
    #handleContentChange;
    /**
     * Detect overflow and update property that is reflected as html attribute
     */
    #detectOverflow;
    #updateButtonStates;
    #handleMenuButtonClick;
    /**
     * @returns html for the menu bar and menu buttons for CampaignWYSIWYG
     */
    get #menubarTemplate() {
        return html `
      <div class="menu-container">
        ${repeat(Object.keys(this.menuButtons).sort((a, b) => this.menuButtons[a].order
            - this.menuButtons[b].order), btn => btn, btnKey => {
            const btn = this.menuButtons[btnKey];
            return html `
              <omni-tooltip>
                <button
                  class="button ${classMap({ 'is-active': btn.active })}"
                  slot="invoker"
                  @click=${() => {
                this.#handleMenuButtonClick(btn.method);
            }}
                >
                  <omni-icon icon-id=${btn.icon} class="is-size-2"></omni-icon>
                </button>
                <div slot="content">${btn.tooltip}</div>
              </omni-tooltip>
            `;
        })}
      </div>
    `;
    }
    /**
     * @returns Primary HTML to be rerendered on the DOM
     */
    render() {
        /* eslint-disable lit-a11y/click-events-have-key-events */
        return html `
      <omni-style>
        <div
          ${ref(this.wysiwygRef)}
          class="wysiwyg ${classMap({
            'is-editing': !this.displayOnly,
            skeleton: this.loadingInitSetup,
        })}"
        >
          ${!this.displayOnly ? this.#menubarTemplate : nothing}
          <div ${ref(this.mentionListRef)} class="mentionList dropdown-content">
            ${this.mentionMatches
            ? repeat(this.mentionMatches, match => match.id, (match, idx) => html `<li
                      class=${classMap({
                'mention-item-selected': this.mentionItemSelected === idx,
                'dropdown-item': true,
                'mention-item': true,
            })}
                      @mouseover=${() => {
                this.mentionItemSelected = idx;
            }}
                      @focus=${(e) => {
                const target = e.target;
                if (target) {
                    const mouseoverEvent = new MouseEvent('mouseover', {
                        bubbles: true,
                        cancelable: true,
                        view: window,
                    });
                    target.dispatchEvent(mouseoverEvent);
                }
            }}
                      @click=${this.#selectMention}
                    >
                      ${match.displayName}
                    </li>`)
            : nothing}
          </div>
          <omni-loading-indicator
            .hidden=${!this.loadingMentions}
          ></omni-loading-indicator>
        </div>
      </omni-style>
    `;
        /* eslint-enable lit-a11y/click-events-have-key-events */
    }
}
__decorate([
    property({ type: String })
], OmniWYSIWYG.prototype, "env", void 0);
__decorate([
    property({ type: String })
], OmniWYSIWYG.prototype, "clientId", void 0);
__decorate([
    property({
        attribute: 'display-only',
        type: Boolean,
        reflect: true,
    })
], OmniWYSIWYG.prototype, "displayOnly", void 0);
__decorate([
    property({
        attribute: 'overflow-visible',
        type: Boolean,
        reflect: true,
    })
], OmniWYSIWYG.prototype, "overflowVisible", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], OmniWYSIWYG.prototype, "overflowed", void 0);
__decorate([
    property({ type: Array })
], OmniWYSIWYG.prototype, "enabledExtensions", void 0);
__decorate([
    property({ type: Boolean })
], OmniWYSIWYG.prototype, "shouldAutofocus", void 0);
__decorate([
    property({ type: String })
], OmniWYSIWYG.prototype, "placeholder", void 0);
__decorate([
    property({
        attribute: 'enable-mentions',
        type: Boolean,
        reflect: true,
    })
], OmniWYSIWYG.prototype, "enableMentions", void 0);
__decorate([
    property({
        attribute: 'mention-types',
        type: Array,
        reflect: true,
        converter: {
            fromAttribute(val) {
                try {
                    // allow single-quotes for html attribute, valid values have none
                    return JSON.parse(val.replaceAll("'", '"'));
                }
                catch {
                    if (!val?.trim() || !CATEGORY_OPTIONS.includes(val.trim()))
                        return undefined;
                    return [val];
                }
            },
            toAttribute(val) {
                if (Array.isArray(val))
                    return JSON.stringify(val);
                return val;
            },
        },
    })
], OmniWYSIWYG.prototype, "mentionTypes", void 0);
__decorate([
    state()
], OmniWYSIWYG.prototype, "menuButtons", void 0);
__decorate([
    state()
], OmniWYSIWYG.prototype, "loadingMentions", void 0);
__decorate([
    state()
], OmniWYSIWYG.prototype, "mentionMatches", void 0);
__decorate([
    state()
], OmniWYSIWYG.prototype, "mentionItemSelected", void 0);
__decorate([
    state()
], OmniWYSIWYG.prototype, "teamOptions", void 0);
__decorate([
    state()
], OmniWYSIWYG.prototype, "loadingInitSetup", void 0);
OmniElement.register('omni-wysiwyg', OmniWYSIWYG);
//# sourceMappingURL=omni-wysiwyg.js.map